/**
 * FRICTION map renderer (PixiJS 8, WebGL2, custom GLSL) — see docs/Rendering.md.
 *
 * Game mode reads `gameStore` every frame: static layers are built once per map, dynamic layers
 * are rebuilt only when the view (a new tick) or the interaction state changes, and per-frame
 * work is interpolation and animation. Input goes back to the store actions; camera requests
 * and reports travel over the bus. Attract mode renders a generated map as a slow cinematic
 * menu background without fog, units or input.
 */
import {
  FACTION_COLORS,
  type FactionId,
  type GameMap,
  generateMap,
  HEX_W,
  HexGrid,
  type HexId,
  hourOfDay,
  MAP_SIZES,
  NO_HEX,
  type PlayerView,
} from '@friction/engine';
import { Application, Container, Graphics } from 'pixi.js';
import { settingsStore } from '../app/settings';
import { bus } from '../game/bus';
import { contactsAt, type GameStoreState, gameStore, isReadOnly } from '../game/store';
import { getLocale } from '../i18n/locale';
import { Camera } from './camera';
import { loadFonts } from './fonts';
import { InputController } from './input';
import { markerScale } from './layers/labels';
import { LightLayer, lightAt } from './layers/light';
import { groundBits, SkyCells, SkyField, SkyLayer } from './layers/weather';
import { mixColor, PAL } from './palette';
import { MapScene, SharedResources } from './scene';

export interface RenderStats {
  /** Frames per second (1 s rolling average). */
  fps: number;
  /** Mean frame interval, ms. */
  frameMs: number;
  /** Mean CPU time per frame spent in update + render submission, ms. */
  cpuMs: number;
  /** Time the last map bake took, ms. */
  bakeMs: number;
}

export interface MapRendererHandle {
  destroy(): void;
  /** Glide the camera to a hex (same as the bus 'focusHex' event); `instant` jumps. */
  focusHex(hex: HexId, zoom?: number, instant?: boolean): void;
  /** Current camera: world-space centre and zoom (css px per world unit). */
  camera(): { x: number; y: number; zoom: number };
  stats(): RenderStats;
  /**
   * Render `frames` frames back to back, waiting for the GPU after each one, and return the mean
   * wall time per frame — a throttling-proof measure of achievable frame rate (lab / perf docs).
   */
  benchmark(frames?: number): { msPerFrame: number; cpuMsPerFrame: number };
  /**
   * The GPU's own time per frame (ms) from timer queries, averaged over `frames` renders — immune
   * to the scheduling noise of hidden or busy tabs. Null where the browser offers no timer queries.
   */
  gpuBenchmark(frames?: number): Promise<number | null>;
}

export interface MapRendererOptions {
  /** Menu background mode: slow cinematic pan over a generated map, no input, no units. */
  attract?: boolean;
  /** Seed for the attract-mode map. */
  seed?: number;
}

type Source = Pick<
  GameStoreState,
  | 'map'
  | 'grid'
  | 'faction'
  | 'view'
  | 'prevView'
  | 'viewReceivedAt'
  | 'tickMs'
  | 'selection'
  | 'inspectedContact'
  | 'hoverHex'
  | 'tool'
  | 'freshEvents'
  | 'truth'
  | 'replay'
  | 'observing'
>;

const reducedMotionQuery =
  typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;

export async function createMapRenderer(
  container: HTMLElement,
  opts: MapRendererOptions = {},
): Promise<MapRendererHandle> {
  await loadFonts();
  const resolution = Math.min(2, window.devicePixelRatio || 1);
  const app = new Application();
  await app.init({
    preference: 'webgl',
    width: Math.max(1, container.clientWidth),
    height: Math.max(1, container.clientHeight),
    resolution,
    autoDensity: true,
    antialias: false,
    background: PAL.table,
    powerPreference: 'high-performance',
    autoStart: false,
    sharedTicker: false,
    eventFeatures: { move: false, globalMove: false, click: false, wheel: false },
  });
  const r = new MapRenderer(app, container, opts);
  return {
    destroy: () => r.destroy(),
    focusHex: (hex, zoom, instant) => r.focus(hex, zoom, instant),
    camera: () => ({ x: r.camera.x, y: r.camera.y, zoom: r.camera.zoom }),
    stats: () => r.stats(),
    benchmark: (frames = 60) => r.benchmark(frames),
    gpuBenchmark: (frames = 60) => r.gpuBenchmark(frames),
  };
}

class MapRenderer {
  readonly camera = new Camera();
  private readonly app: Application;
  private readonly host: HTMLElement;
  private readonly attract: boolean;
  private readonly shared = new SharedResources();
  private readonly world1 = new Container({ label: 'world-print' });
  private readonly world2 = new Container({ label: 'world-live' });
  private readonly ui = new Container({ label: 'screen-ui' });
  private readonly skyCells = new SkyCells();
  private readonly skyField = new SkyField();
  private readonly light = new LightLayer(this.shared.noise, this.skyField);
  private readonly sky = new SkyLayer(this.skyField);
  /** Current lightning flash: world x, y, strength, radius; and when the next one may strike. */
  private flash: [number, number, number, number] = [0, 0, 0, 1];
  private flashAge = 0;
  private nextFlash = 0;
  /** How far the wind has carried the cloud texture (world units), and the game time of that. */
  private drift = { x: 0, y: 0 };
  private driftAt = -1;
  private readonly box = new Graphics();
  private scene: MapScene | null = null;
  private input: InputController | null = null;
  private resizeObs: ResizeObserver;
  private readonly unsub: (() => void)[] = [];
  private raf = 0;
  private last = 0;
  private destroyed = false;
  private attractSource: Source | null = null;
  private attractT = 0;
  private attractPath: number[] = [];
  // change tracking
  private lastView: PlayerView | null = null;
  private lastOwner: Uint8Array | null = null;
  private lastSelection: readonly number[] | null = null;
  private lastHover: HexId = NO_HEX;
  private lastTool = '';
  private lastInspected: number | null = null;
  private colors = new Map<FactionId, number>();
  private lastCameraEmit = 0;
  private emittedVersion = -1;
  private pendingFocus: { hex: HexId; zoom?: number } | null = null;
  // stats
  private frameAcc = 0;
  private cpuAcc = 0;
  private frames = 0;
  private statsAt = 0;
  private stat: RenderStats = { fps: 0, frameMs: 0, cpuMs: 0, bakeMs: 0 };

  constructor(app: Application, host: HTMLElement, opts: MapRendererOptions) {
    this.app = app;
    this.host = host;
    this.attract = !!opts.attract;
    const canvas = app.canvas;
    canvas.style.display = 'block';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.touchAction = 'none';
    if (this.attract) canvas.style.pointerEvents = 'none';
    host.appendChild(canvas);
    app.stage.eventMode = 'none';
    app.stage.addChild(this.world1, this.light.mesh, this.sky.mesh, this.world2, this.ui);
    this.ui.addChild(this.box);

    this.camera.setScreen(app.screen.width, app.screen.height);
    this.light.resize(app.screen.width, app.screen.height);
    this.sky.resize(app.screen.width, app.screen.height);
    this.skyField.resize(app.screen.width, app.screen.height);
    this.resizeObs = new ResizeObserver(() => this.resize());
    this.resizeObs.observe(host);

    if (this.attract) this.attractSource = this.makeAttractSource(opts.seed ?? 1);
    else {
      this.input = new InputController({
        canvas,
        camera: this.camera,
        hexAt: (sx, sy) => this.hexAt(sx, sy),
        hitCounter: (sx, sy) => {
          if (!this.scene) return null;
          const [wx, wy] = this.camera.screenToWorld(sx, sy);
          return this.scene.units.hit(wx, wy);
        },
        ownUnitsInScreenRect: (x0, y0, x1, y1) => {
          if (!this.scene) return [];
          const [a, b] = this.camera.screenToWorld(x0, y0);
          const [c, d] = this.camera.screenToWorld(x1, y1);
          return this.scene.units.ownInRect(a, b, c, d);
        },
        setBox: (r) => this.drawBox(r),
        setHoverCounter: (key) => {
          if (this.scene) this.scene.units.hoverKey = key;
        },
        edgeScroll: () => settingsStore.getState().edgeScroll,
        store: () => gameStore.getState(),
      });
      this.unsub.push(
        bus.on('focusHex', ({ hex, zoom }) => this.focus(hex, zoom)),
        bus.on('panTo', ({ x, y }) => this.camera.glideTo(x, y, undefined, 9)),
      );
    }
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  // ---- public ---------------------------------------------------------------------------------

  focus(hex: HexId, zoom?: number, instant = false): void {
    const grid = this.scene?.grid;
    if (!grid) {
      this.pendingFocus = { hex, zoom };
      return;
    }
    if (hex < 0 || hex >= grid.count) return;
    if (instant) this.camera.set(grid.cx(hex), grid.cy(hex), zoom ?? this.camera.zoom);
    else this.camera.glideTo(grid.cx(hex), grid.cy(hex), zoom);
  }

  stats(): RenderStats {
    return { ...this.stat, bakeMs: this.scene?.bakeMs ?? 0 };
  }

  benchmark(frames: number): { msPerFrame: number; cpuMsPerFrame: number } {
    const gl = (this.app.renderer as unknown as { gl?: WebGL2RenderingContext }).gl;
    const px = new Uint8Array(4);
    let cpu = 0;
    const t0 = performance.now();
    for (let i = 0; i < frames; i++) {
      const c0 = performance.now();
      this.update(1 / 60, c0);
      this.app.render();
      cpu += performance.now() - c0;
      gl?.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
    }
    return { msPerFrame: (performance.now() - t0) / frames, cpuMsPerFrame: cpu / frames };
  }

  async gpuBenchmark(frames: number): Promise<number | null> {
    const gl = (this.app.renderer as unknown as { gl?: WebGL2RenderingContext }).gl;
    const timer = gl?.getExtension('EXT_disjoint_timer_query_webgl2') as {
      TIME_ELAPSED_EXT: number;
      GPU_DISJOINT_EXT: number;
    } | null;
    if (!gl || !timer) return null;
    const queries: WebGLQuery[] = [];
    for (let i = 0; i < frames; i++) {
      const q = gl.createQuery();
      gl.beginQuery(timer.TIME_ELAPSED_EXT, q);
      this.update(1 / 60, performance.now());
      this.app.render();
      gl.endQuery(timer.TIME_ELAPSED_EXT);
      queries.push(q);
    }
    // Query results only become available in a later task.
    let total = 0;
    let timed = 0;
    for (let tries = 0; tries < 50 && queries.length; tries++) {
      await new Promise((r) => setTimeout(r, 20));
      if (gl.getParameter(timer.GPU_DISJOINT_EXT)) break;
      while (queries.length && gl.getQueryParameter(queries[0], gl.QUERY_RESULT_AVAILABLE)) {
        const q = queries.shift() as WebGLQuery;
        total += gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6;
        timed++;
        gl.deleteQuery(q);
      }
    }
    for (const q of queries) gl.deleteQuery(q);
    return timed ? total / timed : null;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    this.resizeObs.disconnect();
    for (const u of this.unsub) u();
    this.input?.destroy();
    this.scene?.destroy();
    this.scene = null;
    this.light.destroy();
    this.sky.destroy();
    this.skyField.destroy();
    this.shared.destroy();
    this.app.destroy({ removeView: true }, { children: true });
  }

  // ---- frame ------------------------------------------------------------------------------------

  private readonly tick = (now: number): void => {
    if (this.destroyed) return;
    this.raf = requestAnimationFrame(this.tick);
    const dt = Math.min(0.1, Math.max(0, (now - this.last) / 1000));
    this.frameAcc += now - this.last;
    this.last = now;
    const t0 = performance.now();
    this.update(dt, now);
    this.app.render();
    this.cpuAcc += performance.now() - t0;
    this.frames++;
    if (now - this.statsAt > 1000) {
      this.stat = {
        fps: (this.frames * 1000) / (now - this.statsAt),
        frameMs: this.frameAcc / this.frames,
        cpuMs: this.cpuAcc / this.frames,
        bakeMs: this.scene?.bakeMs ?? 0,
      };
      this.frames = 0;
      this.frameAcc = 0;
      this.cpuAcc = 0;
      this.statsAt = now;
    }
  };

  private update(dt: number, now: number): void {
    const s: Source = this.attractSource ?? gameStore.getState();
    if (s.map !== this.scene?.map) this.setMap(s.map, s.grid, s);
    const scene = this.scene;
    if (!scene) return;
    const settings = settingsStore.getState();
    const reducedMotion = settings.reducedMotion || !!reducedMotionQuery?.matches;

    // ---- camera
    this.input?.update(dt);
    if (this.attract) this.driftAttract(dt, reducedMotion);
    this.camera.update(dt);
    const cam = this.camera;
    const zoom = cam.zoom;
    const res = this.app.renderer.resolution;
    for (const w of [this.world1, this.world2]) {
      w.scale.set(zoom);
      w.position.set(cam.screenW / 2 - cam.x * zoom, cam.screenH / 2 - cam.y * zoom);
    }

    // ---- dynamic data (new tick / interaction)
    scene.units.setStyle(settings.unitStyle, getLocale());
    const view = s.view;
    if (view && view !== this.lastView) this.onView(scene, s, view, reducedMotion);
    if (
      view &&
      (s.selection !== this.lastSelection ||
        s.hoverHex !== this.lastHover ||
        s.tool !== this.lastTool ||
        s.inspectedContact !== this.lastInspected ||
        view !== this.lastView)
    ) {
      this.lastSelection = s.selection;
      this.lastHover = s.hoverHex;
      this.lastTool = s.tool;
      this.lastInspected = s.inspectedContact;
      scene.units.selected = new Set(s.selection);
      scene.units.inspected = s.inspectedContact;
      scene.orders.rebuild({
        view,
        prev: s.prevView,
        selection: s.selection,
        hoverHex: s.hoverHex,
        tool: s.tool,
        hostileAtHover: s.hoverHex !== NO_HEX && contactsAt(view, s.hoverHex).length > 0,
        colors: this.colors,
        preview: !isReadOnly(s),
      });
    }
    this.lastView = view;

    // ---- per frame
    const t = view && s.tickMs > 0 ? Math.min(1, Math.max(0, (now - s.viewReceivedAt) / s.tickMs)) : 1;
    const hour = view ? hourOfDay(view.tick) - 1 + t : 16.8;
    const { color, night } = lightAt(hour);
    const markerUnit = markerScale(zoom) / zoom;
    scene.terrain.update(zoom, res);
    scene.features.update(zoom, res);
    scene.ice.setZoom(zoom, res);
    scene.overlay.update(zoom, res, now / 1000);
    scene.borders.update(zoom, res, markerUnit, settings.showGrid && !this.attract);
    scene.labels.update(cam, res, dt, now, night, reducedMotion);
    scene.units.frame(t, zoom, now, reducedMotion);
    scene.orders.frame(t, zoom, res, markerUnit, (id) => scene.units.positionOf(id));
    scene.battles.frame(dt, now, markerUnit, cam.view(), night, reducedMotion);
    this.light.set(color, night, this.attract ? 0.8 : 1, this.attract ? 1.7 : 1);
    this.updateWeather(scene, s, t, dt, night, reducedMotion);
    const lit =
      (Math.round(color[0] * 255) << 16) | (Math.round(color[1] * 255) << 8) | Math.round(color[2] * 255);
    this.world2.tint = mixColor(0xffffff, lit, 0.35);
    scene.battles.smokeMesh.tint = lit;
    this.shared.atlas.flush();

    if (cam.version !== this.emittedVersion && now - this.lastCameraEmit > 60 && !this.attract) {
      this.emittedVersion = cam.version;
      this.lastCameraEmit = now;
      const v = cam.view();
      bus.emit('camera', { x: v.x0, y: v.y0, width: v.x1 - v.x0, height: v.y1 - v.y0, zoom });
    }
  }

  private onView(scene: MapScene, s: Source, view: PlayerView, reducedMotion: boolean): void {
    this.colors = new Map(view.factions.map((f) => [f.id, f.color]));
    scene.overlay.setFactionColors(this.colors);
    scene.borders.setFactions(
      new Map(view.factions.map((f) => [f.id, { color: f.color, team: f.team }])),
      view.faction,
    );
    const vis = view.visible;
    const weather = view.weather ?? null;
    scene.overlay.setHexes(
      view.owner,
      vis,
      weather ? groundBits(scene.map, scene.grid, weather.conditions) : null,
    );
    scene.ice.update(scene.map, scene.grid, weather?.conditions ?? null);
    if (view.owner !== this.lastOwner) {
      if (!this.lastOwner || !sameBytes(this.lastOwner, view.owner)) scene.borders.rebuild(view.owner, true);
      this.lastOwner = view.owner;
    }
    scene.units.rebuild(view, s.prevView, this.colors, s.truth);
    scene.battles.rebuild(view, this.colors);
    scene.battles.events(s.freshEvents, this.colors, reducedMotion);
  }

  private setMap(map: GameMap | null, grid: HexGrid | null, s: Source): void {
    this.scene?.destroy();
    this.scene = null;
    this.world1.removeChildren();
    this.world2.removeChildren();
    this.lastView = null;
    this.lastOwner = null;
    this.lastSelection = null;
    if (!map) return;
    const g = grid ?? new HexGrid(map.width, map.height);
    const scene = new MapScene(map, g, this.shared, this.app.renderer.resolution);
    this.scene = scene;
    this.world1.addChild(
      scene.terrain.mesh,
      scene.features.container,
      scene.ice.mesh,
      scene.overlay.mesh,
      scene.borders.container,
      scene.labels.container,
    );
    this.world2.addChild(
      scene.labels.glowMesh,
      scene.orders.container,
      scene.units.container,
      scene.battles.container,
    );
    this.skyField.setNeat(scene.sheet.neat);
    this.camera.setBounds({
      x0: scene.sheet.sheet.x0 - 40,
      y0: scene.sheet.sheet.y0 - 40,
      x1: scene.sheet.sheet.x1 + 40,
      y1: scene.sheet.sheet.y1 + 40,
    });

    // static colouring for maps without a view (attract mode)
    if (!s.view) {
      this.colors = new Map(
        Array.from(
          { length: map.factionCount },
          (_, i) => [i, FACTION_COLORS[i % FACTION_COLORS.length]] as [number, number],
        ),
      );
      scene.overlay.setFactionColors(this.colors);
      scene.overlay.setHexes(map.initialOwner, null);
      scene.borders.setFactions(
        new Map([...this.colors].map(([id, color]) => [id, { color, team: id }])),
        null,
      );
      scene.borders.rebuild(map.initialOwner, false);
    }

    // initial camera: the centre of our army (where the war is), else the capital, else the sheet
    const capital = s.view ? map.cities[s.view.me.capitalCity] : undefined;
    const own = s.view ? s.view.units.filter((u) => u.faction === s.view?.faction) : [];
    if (this.pendingFocus) {
      const { hex, zoom } = this.pendingFocus;
      this.pendingFocus = null;
      this.camera.set(g.cx(hex), g.cy(hex), zoom ?? 1.1);
    } else if (own.length && !this.attract) {
      const x = own.reduce((a, u) => a + g.cx(u.hex), 0) / own.length;
      const y = own.reduce((a, u) => a + g.cy(u.hex), 0) / own.length;
      this.camera.set(x, y, 0.85);
    } else if (capital && !this.attract) this.camera.set(g.cx(capital.hex), g.cy(capital.hex), 1.1);
    else
      this.camera.set(
        (scene.sheet.sheet.x0 + scene.sheet.sheet.x1) / 2,
        (scene.sheet.sheet.y0 + scene.sheet.sheet.y1) / 2,
        0,
      );
    if (this.attract) this.planAttract(scene);
  }

  // ---- weather ----------------------------------------------------------------------------------

  /**
   * Per frame: interpolate the weather cells for the light layer (cloud shadows, storms,
   * lightning) and the sky layer (rain, snow); drift the morning haze with the wind.
   */
  private updateWeather(
    scene: MapScene,
    s: Source,
    t: number,
    dt: number,
    night: number,
    still: boolean,
  ): void {
    const cam = this.camera;
    const w = s.view?.weather ?? null;
    this.skyCells.update(w, s.prevView?.weather ?? null, t, cam.view(), scene.map, scene.grid);
    const wind = w?.wind ?? { x: 0, y: 0 };
    // Cloud billows drift with the wind at game speed (hex units per game hour).
    const gameTime = (s.view?.tick ?? 0) + t;
    const dh = this.driftAt < 0 ? 0 : Math.min(3, Math.max(0, gameTime - this.driftAt));
    this.driftAt = gameTime;
    this.drift.x += wind.x * HEX_W * dh;
    this.drift.y += wind.y * HEX_W * dh;
    // Haze drifts at a gentle, readable pace (world units per real second), not game speed.
    scene.overlay.setWind(still ? 0 : wind.x * HEX_W * 0.2, still ? 0 : wind.y * HEX_W * 0.2);
    this.lightning(dt, still);
    this.skyField.render(this.app.renderer, this.skyCells, cam);
    this.light.setSky(this.skyCells.count > 0, cam, this.flash, this.drift);
    this.sky.update(this.skyCells, cam, wind, night, dt, still);
  }

  /** Now and then a flash in the heart of the strongest thunderstorm over the screen. */
  private lightning(dt: number, still: boolean): void {
    const cells = this.skyCells;
    this.flashAge += dt;
    // Two quick pulses, fading in a third of a second.
    const a = this.flashAge;
    this.flash[2] =
      a < 0.35 ? 0.42 * Math.exp(-a / 0.08) + (a > 0.12 ? 0.3 * Math.exp(-(a - 0.12) / 0.07) : 0) : 0;
    if (still || cells.storm < 0.7 || cells.count === 0) return;
    this.nextFlash -= dt;
    if (this.nextFlash > 0) return;
    this.nextFlash = 3 + Math.random() * 9;
    let best = -1;
    for (let i = 0; i < cells.count; i++)
      if (!cells.b[i * 4 + 3] && (best < 0 || cells.b[i * 4 + 2] > cells.b[best * 4 + 2])) best = i;
    if (best < 0 || cells.b[best * 4 + 2] < 0.7) return;
    const r = Math.min(cells.b[best * 4], cells.b[best * 4 + 1]) * 0.35;
    const ang = Math.random() * Math.PI * 2;
    const x = cells.a[best * 4] + Math.cos(ang) * r * Math.random();
    const y = cells.a[best * 4 + 1] + Math.sin(ang) * r * Math.random();
    this.flash = [x, y, 0, r * 1.4 + 60];
    this.flashAge = 0;
    bus.emit('lightning', { x, y, strength: cells.b[best * 4 + 2] });
  }

  // ---- attract mode -----------------------------------------------------------------------------

  private makeAttractSource(seed: number): Source {
    const size = MAP_SIZES.medium;
    const map = generateMap({ seed, width: size.width, height: size.height, factionCount: 4 });
    return {
      map,
      grid: new HexGrid(map.width, map.height),
      faction: 0,
      view: null,
      prevView: null,
      viewReceivedAt: 0,
      tickMs: 0,
      selection: [],
      inspectedContact: null,
      hoverHex: NO_HEX,
      tool: 'select',
      freshEvents: [],
      truth: null,
      replay: false,
      observing: null,
    };
  }

  /** Waypoints: the capitals and big cities, visited in a loop. */
  private planAttract(scene: MapScene): void {
    const g = scene.grid;
    const pts = scene.map.cities
      .filter((c) => c.size >= 2 || c.capitalOf !== 255)
      .sort(
        (a, b) =>
          Math.atan2(g.cy(a.hex) - g.worldHeight / 2, g.cx(a.hex) - g.worldWidth / 2) -
          Math.atan2(g.cy(b.hex) - g.worldHeight / 2, g.cx(b.hex) - g.worldWidth / 2),
      )
      .slice(0, 9)
      .flatMap((c) => [g.cx(c.hex), g.cy(c.hex)]);
    const raw =
      pts.length >= 6
        ? pts
        : [
            g.worldWidth * 0.3,
            g.worldHeight * 0.4,
            g.worldWidth * 0.7,
            g.worldHeight * 0.45,
            g.worldWidth * 0.5,
            g.worldHeight * 0.65,
          ];
    // keep the drifting view over the map, never over the sheet margin
    const n = scene.sheet.neat;
    const hw = Math.min((n.x1 - n.x0) / 2, this.camera.screenW / 2 / 0.66);
    const hh = Math.min((n.y1 - n.y0) / 2, this.camera.screenH / 2 / 0.66);
    this.attractPath = raw.map((v, i) =>
      i % 2 === 0 ? Math.min(n.x1 - hw, Math.max(n.x0 + hw, v)) : Math.min(n.y1 - hh, Math.max(n.y0 + hh, v)),
    );
    this.attractT = (scene.map.seed % 97) / 97;
  }

  /** Slow Catmull-Rom drift through the waypoints with a gentle breathing zoom. */
  private driftAttract(dt: number, reducedMotion: boolean): void {
    const p = this.attractPath;
    const n = p.length >> 1;
    if (n < 3) return;
    if (!reducedMotion) this.attractT += dt / 26;
    const u = this.attractT % n;
    const i = Math.floor(u);
    const f = u - i;
    const P = (k: number): [number, number] => {
      const j = (((i + k) % n) + n) % n;
      return [p[j * 2], p[j * 2 + 1]];
    };
    const [p0, p1, p2, p3] = [P(-1), P(0), P(1), P(2)];
    const cr = (a: number, b: number, c: number, d: number) =>
      0.5 *
      (2 * b + (-a + c) * f + (2 * a - 5 * b + 4 * c - d) * f * f + (-a + 3 * b - 3 * c + d) * f * f * f);
    const x = cr(p0[0], p1[0], p2[0], p3[0]);
    const y = cr(p0[1], p1[1], p2[1], p3[1]);
    const zoom = 0.82 + 0.16 * Math.sin(this.attractT * 0.9);
    this.camera.set(x, y, zoom);
  }

  // ---- helpers ------------------------------------------------------------------------------------

  private hexAt(sx: number, sy: number): HexId {
    const grid = this.scene?.grid;
    if (!grid) return NO_HEX;
    const [wx, wy] = this.camera.screenToWorld(sx, sy);
    return grid.fromWorld(wx, wy);
  }

  private drawBox(r: { x0: number; y0: number; x1: number; y1: number } | null): void {
    const b = this.box;
    b.clear();
    if (!r) return;
    b.rect(r.x0, r.y0, r.x1 - r.x0, r.y1 - r.y0)
      .fill({ color: PAL.brassLight, alpha: 0.08 })
      .stroke({ width: 1.2, color: PAL.brassLight, alpha: 0.9, alignment: 0 });
  }

  private resize(): void {
    const w = Math.max(1, this.host.clientWidth);
    const h = Math.max(1, this.host.clientHeight);
    const res = Math.min(2, window.devicePixelRatio || 1);
    if (w === this.app.screen.width && h === this.app.screen.height && res === this.app.renderer.resolution)
      return;
    this.app.renderer.resize(w, h, res);
    this.camera.setScreen(w, h);
    this.light.resize(w, h);
    this.sky.resize(w, h);
    this.skyField.resize(w, h);
  }
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
